<template>
  <div class="record-container">
    <!-- 顶部横幅 -->
    <TopHeader />

    <!-- 主内容 -->
    <div class="main-wrapper">
      <div class="page-header">
        <div class="back-link" @click="goBack">
          <img src="@/assets/image/back.png" class="back-icon" />
          <span>返回首页</span>
        </div>
        <h2>考试记录</h2>
      </div>

      <!-- 加载中 -->
      <div v-if="loading" class="loading-box">加载中...</div>

      <!-- 无记录 -->
      <div v-else-if="records.length === 0" class="empty-box">
        <p>暂无考试记录</p>
      </div>

      <!-- 考试记录列表 -->
      <div v-else class="record-list">
        <div v-for="item in records" :key="item.recordId" class="record-item">
          <div class="record-main">
            <div class="record-info">
              <div class="record-title">
                <span class="title-icon">🎓</span>
                <span>{{ item.examTitle }}</span>
              </div>
              <div class="record-subject">考试科目：{{ item.courseName || '未知科目' }}</div>
              <div class="record-meta">
                <span class="meta-item">
                  <span class="meta-icon">■</span>
                  考试时间：{{ formatTime(item.submitTime) }}
                </span>
                <span class="meta-item">
                  <span class="meta-icon">●</span>
                  用时：{{ formatDuration(item.usedTime) }}
                </span>
              </div>
            </div>
            <div class="record-score" :class="getScoreClass(item)">{{ item.score }}分</div>
          </div>
          <div class="record-stats">
            <span class="stat correct">正确：{{ item.correctCount }}</span>
            <span class="stat wrong">错误：{{ item.wrongCount }}</span>
            <span class="stat total">总题：{{ item.totalCount }}</span>
          </div>
          <div class="record-footer">
            <button class="detail-btn" @click="viewDetail(item)">查看详情</button>
          </div>
        </div>
        
        <!-- 分页 -->
        <div v-if="total > pageSize" class="pagination">
          <button class="page-btn" :disabled="pageNum <= 1" @click="changePage(pageNum - 1)">上一页</button>
          <span class="page-info">{{ pageNum }} / {{ totalPages }}</span>
          <button class="page-btn" :disabled="pageNum >= totalPages" @click="changePage(pageNum + 1)">下一页</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TopHeader from '@/components/TopHeader.vue'
import { getExamRecords } from '@/api/exam'
import { encodeId } from '@/utils/idEncoder'

export default {
  name: 'ExamRecordPage',
  components: {
    TopHeader
  },
  data() {
    return {
      loading: true,
      records: [],
      pageNum: 1,
      pageSize: 10,
      total: 0
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    }
  },
  created() {
    this.loadRecords()
  },
  methods: {
    async loadRecords() {
      this.loading = true
      try {
        const res = await getExamRecords(this.pageNum, this.pageSize)
        this.records = res.data.list || []
        this.total = res.data.total || 0
      } catch (error) {
        console.error('加载考试记录失败', error)
      } finally {
        this.loading = false
      }
    },
    changePage(page) {
      this.pageNum = page
      this.loadRecords()
      window.scrollTo(0, 0)
    },
    goBack() {
      this.$router.push('/home')
    },
    viewDetail(item) {
      this.$router.push('/exam-record-detail/' + encodeId(item.recordId))
    },
    formatTime(time) {
      if (!time) return '-'
      const d = new Date(time)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const hour = String(d.getHours()).padStart(2, '0')
      const minute = String(d.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hour}:${minute}`
    },
    formatDuration(seconds) {
      if (!seconds) return '-'
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      const s = seconds % 60
      if (h > 0) {
        return `${h}小时${m}分钟`
      }
      return `${m}分${s}秒`
    },
    getScoreClass(item) {
      if (!item.totalScore) return ''
      const percent = (item.score / item.totalScore) * 100
      if (percent >= 80) return 'excellent'
      if (percent >= 60) return 'good'
      return 'poor'
    }
  }
}
</script>

<style scoped>
.record-container {
  min-height: 100vh;
  background: #f5f5f5;
  padding-top: 70px;
}

.main-wrapper {
  max-width: 900px;
  margin: 30px auto;
  padding: 0 20px;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 25px;
}

.back-link {
  display: flex;
  align-items: center;
  gap: 6px;
  color: #666;
  cursor: pointer;
  font-size: 14px;
}

.back-icon {
  width: 16px;
  height: 16px;
}

.back-link:hover {
  color: #3d8c40;
}

.page-header h2 {
  font-size: 20px;
  color: #333;
  font-weight: 500;
  margin: 0;
}

.loading-box, .empty-box {
  background: #fff;
  border-radius: 8px;
  padding: 60px 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.record-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.record-item {
  background: #fff;
  border-radius: 8px;
  padding: 25px;
}

.record-main {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.record-info {
  flex: 1;
}

.record-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  color: #333;
  font-weight: 500;
  margin-bottom: 10px;
}

.title-icon {
  font-size: 18px;
}

.record-subject {
  font-size: 14px;
  color: #666;
  margin-bottom: 12px;
}

.record-meta {
  display: flex;
  gap: 30px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #999;
}

.meta-icon {
  font-size: 8px;
}

.record-score {
  font-size: 36px;
  font-weight: 600;
  color: #333;
}

.record-score.excellent {
  color: #3d8c40;
}

.record-score.good {
  color: #f0ad4e;
}

.record-score.poor {
  color: #e74c3c;
}

.record-stats {
  display: flex;
  gap: 20px;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #f0f0f0;
}

.stat {
  font-size: 13px;
  color: #666;
}

.stat.correct {
  color: #3d8c40;
}

.stat.wrong {
  color: #e74c3c;
}

.record-footer {
  display: flex;
  justify-content: flex-end;
  margin-top: 15px;
}

.detail-btn {
  padding: 10px 30px;
  background: #3d8c40;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.detail-btn:hover {
  background: #2e7d32;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-top: 30px;
  padding: 20px 0;
}

.page-btn {
  padding: 10px 25px;
  background: #fff;
  color: #3d8c40;
  border: 1px solid #3d8c40;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.page-btn:hover:not(:disabled) {
  background: #3d8c40;
  color: #fff;
}

.page-btn:disabled {
  background: #f5f5f5;
  color: #ccc;
  border-color: #ddd;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .main-wrapper {
    margin: 20px auto;
    padding: 0 15px;
  }

  .record-item {
    padding: 20px 15px;
  }

  .record-main {
    flex-direction: column;
    gap: 15px;
  }

  .record-score {
    font-size: 28px;
    align-self: flex-end;
  }

  .record-meta {
    flex-direction: column;
    gap: 8px;
  }

  .record-stats {
    flex-wrap: wrap;
    gap: 15px;
  }

  .detail-btn {
    width: 100%;
  }
}
</style>
